/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  formatFecha,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario, tituloBase } from "../../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Sidebar } from "../../../../shared";
import { Divider } from "primereact/divider";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

export default function EstadoCertificado() {
  const [estadoCertificado, setEstadoCertificado] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderEliminar, setLoaderEliminar] = useState(false);
  const { estadoCertificadoId } = useParams();
  const toast = useRef(null);

  useEffect(() => {
    cargarEstadoCertificado();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarEstadoCertificado() {
    accessAPI(
      "GET",
      `admin/config/estadocertificado/${estadoCertificadoId}`,
      null,
      (respuesta) => {
        setEstadoCertificado(respuesta);
        document.title = respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al cargar el estado de certificado",
          sticky: true
        });
      }
    );
  }

  function editarEstadoSubmit(respuestasFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/config/estadocertificado/${estadoCertificadoId}`,
      respuestasFormulario,
      (respuesta) => {
        setEstadoCertificado(respuesta);
        document.title = respuesta.nombre;
        setSidebarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Estado de certificado actualizado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Error al actualizar el estado de certificado",
          sticky: true
        });
        setLoaderEditar(false);
      }
    );
  }

  function eliminarEstadoCertificado() {
    confirmDialog({
      message: `¿Eliminar el estado "${estadoCertificado.nombre}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        setLoaderEliminar(true);
        accessAPI(
          "DELETE",
          `admin/config/estadocertificado/${estadoCertificado.id}`,
          null,
          (respuesta) => {
            setLoaderEliminar(false);
            setEstadoCertificado(respuesta);
            toast.current?.show({
              severity: "success",
              summary: "Eliminado",
              detail: "Estado de certificado eliminado"
            });
          },
          (respuesta) => {
            setLoaderEliminar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg ||
                "Error al eliminar el estado de certificado",
              sticky: true
            });
          }
        );
      }
    });
  }

  const eliminado = estadoCertificado && !estadoCertificado.disponible;
  const tieneCertificados = estadoCertificado?._count?.certificado > 0;

  const editarFormConfig = estadoCertificado
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: estadoCertificado.nombre
              },
              {
                nombre: "vigentebool",
                tipoitemformularioid: 6,
                etiqueta: "Es un estado vigente",
                respuesta: estadoCertificado.vigentebool
              },
              {
                nombre: "bajadefinitiva",
                tipoitemformularioid: 6,
                etiqueta: "Es un estado de baja definitiva",
                respuesta: estadoCertificado.bajadefinitiva
              },
              {
                nombre: "esvencimiento",
                tipoitemformularioid: 6,
                etiqueta: "Es el estado de vencimiento (sólo uno)",
                respuesta: estadoCertificado.esvencimiento
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarEstadoSubmit,
            validar: true
          }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {!estadoCertificado && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {estadoCertificado && (
        <>
          <Titulo
            urlVolver="/estadocertificado"
            etiquetas={
              !eliminado && tieneCertificados
                ? [{ texto: "No se puede eliminar", error: true }]
                : []
            }
            botones={
              !eliminado && !tieneCertificados && (
                <Boton
                  rojo
                  callback={eliminarEstadoCertificado}
                  loader={loaderEliminar}
                >
                  Eliminar
                </Boton>
              )
            }
          >
            {eliminado ? (
              <span css={{ color: "var(--rojo)" }}>Estado eliminado</span>
            ) : (
              estadoCertificado.nombre
            )}
          </Titulo>

          <Card>
            <Titulo
              botones={
                !eliminado && (
                  <Boton icono negro callback={() => setSidebarEditar(true)}>
                    <i className="pi pi-pencil" />
                  </Boton>
                )
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Nombre">
                {estadoCertificado.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Estado vigente">
                {estadoCertificado.vigentebool ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Baja definitiva">
                {estadoCertificado.bajadefinitiva ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Estado de vencimiento">
                {estadoCertificado.esvencimiento ? "Sí" : "No"}
              </CampoInformacion>
            </div>
          </Card>

          {tieneCertificados && (
            <Card css={{ marginTop: "1em" }}>
              <Titulo>Certificados en este estado</Titulo>
              <p>
                Hay{" "}
                <span css={{ color: "var(--verde)", fontWeight: "bold" }}>
                  {estadoCertificado._count.certificado}
                </span>{" "}
                certificados en este estado, sólo podrá editar el nombre o si es
                baja definitiva. Para editar la vigencia, no puede haber ningún
                certificado con este estado asignado.
              </p>
              <Divider />
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.25em"
                }}
              >
                {estadoCertificado.certificado.map((certificado) => (
                  <Boton
                    key={certificado.id}
                    uri={`/proceso/${certificado.proceso.id}/certificado/${certificado.id}`}
                    texto
                  >
                    {`${certificado.numero} - ${certificado.proceso.cliente.razonsocial} - ${formatFecha(certificado.fechaemision)}`}
                  </Boton>
                ))}
              </div>
            </Card>
          )}

          <Sidebar
            visible={sidebarEditar}
            position="right"
            onHide={() => setSidebarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar estado de certificado</Titulo>
            {editarFormConfig && (
              <Formulario
                key={
                  estadoCertificado.id + "-" + estadoCertificado.nombre
                }
                config={editarFormConfig}
                loader={loaderEditar}
              />
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
